import React, { CSSProperties } from 'react'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  children: React.ReactNode
}

const baseStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '6px',
  fontFamily: 'system-ui, sans-serif',
  fontWeight: 500,
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  whiteSpace: 'nowrap',
  outline: 'none',
}

const variantStyles: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: '#6366f1',
    color: '#ffffff',
  },
  secondary: {
    background: '#f1f5f9',
    color: '#334155',
    border: '1px solid #e2e8f0',
  },
  ghost: {
    background: 'transparent',
    color: '#64748b',
  },
  danger: {
    background: '#ef4444',
    color: '#ffffff',
  },
}

const sizeStyles: Record<ButtonSize, CSSProperties> = {
  sm: { padding: '4px 10px', fontSize: '12px', height: '28px' },
  md: { padding: '6px 14px', fontSize: '14px', height: '34px' },
  lg: { padding: '10px 20px', fontSize: '15px', height: '42px' },
}

const Spinner = () => (
  <span
    style={{
      width: '14px',
      height: '14px',
      border: '2px solid currentColor',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      display: 'inline-block',
      animation: 'spin 0.6s linear infinite',
    }}
  />
)

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  style,
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const [hovered, setHovered] = React.useState(false)

  const hoverOverlay: CSSProperties =
    hovered && !disabled && !loading
      ? variant === 'primary'
        ? { background: '#4f46e5' }
        : variant === 'danger'
        ? { background: '#dc2626' }
        : variant === 'secondary'
        ? { background: '#e2e8f0' }
        : { background: 'rgba(0,0,0,0.06)' }
      : {}

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <button
        {...props}
        disabled={disabled || loading}
        onMouseEnter={(e) => { setHovered(true); onMouseEnter?.(e) }}
        onMouseLeave={(e) => { setHovered(false); onMouseLeave?.(e) }}
        style={{
          ...baseStyle,
          ...variantStyles[variant],
          ...sizeStyles[size],
          ...hoverOverlay,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled || loading ? 'not-allowed' : 'pointer',
          ...style,
        }}
      >
        {loading && <Spinner />}
        {children}
      </button>
    </>
  )
}
